import isEqual from 'lodash/isEqual';
import {
  AddProfileMedicationDto,
  ChangeMedicationStatusDto,
  UpdateProfileMedicationDto,
} from 'woofwoof-api';
import { MedicationsFormModalValuesProps } from './medicationFormSchema';

/**
 * Fields the vet left blank are omitted, not sent as `''`. An empty string is
 * not a valid enum value, and a payload carrying one is rejected whole — which
 * is what a save that "silently does nothing" looks like from the outside.
 */
function omitBlank(entries: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(entries).filter(
      ([, value]) => value !== '' && value !== undefined && value !== null,
    ),
  );
}

/** The structured dosing and clinical fields of the 2026-08 model. */
export function buildDosingPayload(
  formData: MedicationsFormModalValuesProps,
): Record<string, unknown> {
  return omitBlank({
    strengthValue: formData.strengthValue,
    strengthUnit: formData.strengthUnit,
    strengthUnitCustom: formData.strengthUnitCustom,
    form: formData.form,
    formCustom: formData.formCustom,
    quantityValue: formData.quantityValue,
    quantityCustom: formData.quantityCustom,
    dosagePending: formData.dosagePending,
    frequencyType: formData.frequencyType,
    frequencyValue: formData.frequencyValue,
    frequencyCustom: formData.frequencyCustom,
    durationType: formData.durationType,
    durationValue: formData.durationValue,
    durationCustom: formData.durationCustom,
    dosageSchedule: formData.dosageSchedule,
    indication: formData.indication,
    adverseReaction: formData.adverseReaction,
    adverseReactionNote: formData.adverseReactionNote,
    startedAt: formData.startedAt
      ? new Date(formData.startedAt).toISOString()
      : undefined,
  });
}

/**
 * A lifecycle move, which the dedicated status endpoint handles rather than an
 * ordinary edit — it writes the end date, the reason and the history entry.
 */
export function buildStatusChangePayload(
  formData: MedicationsFormModalValuesProps,
  visitId?: string,
): ChangeMedicationStatusDto {
  return {
    status: formData.status,
    ...(formData.status === 'STOPPED'
      ? {
          stopReason: formData.stopReason || undefined,
          stopReasonNote: formData.stopReasonNote || undefined,
          endedAt: formData.endedAt
            ? new Date(formData.endedAt).toISOString()
            : undefined,
        }
      : {}),
    ...(visitId ? { visitId } : {}),
  } as ChangeMedicationStatusDto;
}

/** What a status change owns; the rest of the form edits the record itself. */
const LIFECYCLE_FIELDS: string[] = [
  'status',
  'stopReason',
  'stopReasonNote',
  'endedAt',
];

/**
 * Whether the form edits the medication itself, beyond its status. The linked
 * reminder counts: it is saved with the medication's own write (R4-1).
 */
export function isMedicationRecordChanged(
  values: MedicationsFormModalValuesProps,
  initial: MedicationsFormModalValuesProps,
): boolean {
  return (
    Object.keys(initial) as Array<keyof MedicationsFormModalValuesProps>
  ).some(
    (field) =>
      !LIFECYCLE_FIELDS.includes(field) &&
      !isEqual(values[field], initial[field]),
  );
}

/**
 * What the profile-scoped endpoints take. The reminder is chained separately
 * on those surfaces, so its fields stay out of this payload.
 */
export function buildMedicationProfilePayload(
  formData: MedicationsFormModalValuesProps,
): AddProfileMedicationDto & UpdateProfileMedicationDto {
  return {
    ...buildDosingPayload(formData),
    medicineProductId: formData.medicineProductId,
    name: formData.name,
    preparationType: formData.preparationType,
    dosage: formData.dosage,
    dosageInterval: formData.dosageInterval,
    notes: formData.notes ?? '',
  } as AddProfileMedicationDto & UpdateProfileMedicationDto;
}
